import { FC } from 'react';

import { FakeLink } from 'components/fake-link/FakeLink';
import { FormMessage } from 'components/forms/FormMessage';
import { CustomLink } from 'components/link/Link';
import ChatWidget from 'services/ChatWidget';
import Location from 'services/Location';
import { shouldHideSignup } from 'helpers/should-hide-signup';
import { getItem } from 'helpers/storage/local-storage';
import { LocalStorageKey } from 'constants/local-storage';
import { FormMessageType } from 'types/form';

export const SigninException: FC = () => {
  const identityException = Location.getUrlParam('identity_exception');
  const exceptionDetails = Location.getUrlParam('exception_details');
  const idp = Location.getUrlParam('idp');
  const shouldHideSignUp = shouldHideSignup();
  const signinMethod = getItem(LocalStorageKey.SigninMethod);

  // @todo temporary workaround as SAML project is near, can be cleaned up afterwards
  const state = Location.getUrlParam('state');

  if (
    !identityException &&
    state?.includes('redirectTo') &&
    state?.includes('wrong-sso') &&
    document.referrer.includes(import.meta.env.VITE_APP_LC_APP_URL)
  ) {
    return (
      <FormMessage type={FormMessageType.Error}>
        Incorrect email or single sign-on disabled
      </FormMessage>
    );
  }

  if (!identityException) {
    return null;
  }

  switch (identityException) {
    case 'invalid_request':
      return (
        <FormMessage type={FormMessageType.Error}>
          There was a problem signing in to your account. Please try again. If the problem persists,{' '}
          <FakeLink onClick={ChatWidget.handleContactSupport}>contact us</FakeLink> to get help.
        </FormMessage>
      );

    // saml error redirected from rest-api
    case 'wrong_sso':
      return (
        <FormMessage type={FormMessageType.Error}>
          Incorrect email or single sign-on disabled
        </FormMessage>
      );

    case 'unauthorized':
      if (exceptionDetails === 'mfa_protected') {
        return null;
      }

      if (shouldHideSignUp) {
        return (
          <FormMessage type={FormMessageType.Error}>
            Incorrect email or password. Please try again or{' '}
            <CustomLink to="/forgot-password">reset your password</CustomLink>.
          </FormMessage>
        );
      }

      return (
        <FormMessage type={FormMessageType.Error}>
          Incorrect email or password. Please try again,{' '}
          <CustomLink to="/forgot-password">reset your password</CustomLink>, or{' '}
          <CustomLink to="/signup">sign up</CustomLink>.
        </FormMessage>
      );

    case 'server_error':
      return (
        <FormMessage type={FormMessageType.Error}>
          There was a problem with signing in to your account. Please try again. If the problem
          persists, <FakeLink onClick={ChatWidget.handleContactSupport}>contact us</FakeLink> to get
          help.
        </FormMessage>
      );

    case 'resource_not_found':
      if (shouldHideSignUp) {
        return (
          <FormMessage type={FormMessageType.Error}>
            Such account doesn&apos;t exist. Try a different one.
          </FormMessage>
        );
      }

      if (idp) {
        return (
          <FormMessage type={FormMessageType.Error}>
            We couldn&apos;t find that account. <CustomLink to="/signup">Sign up</CustomLink> or try
            another login method.
          </FormMessage>
        );
      }

      return (
        <FormMessage type={FormMessageType.Error}>
          Such account doesn&apos;t exist. <CustomLink to="/signup">Sign up</CustomLink> or try a
          different one.
        </FormMessage>
      );

    case 'temporarily_unavailable':
      return (
        <FormMessage type={FormMessageType.Error}>
          Too many login attempts. Please try again later.{' '}
          <FakeLink onClick={ChatWidget.handleContactSupport}>Contact us</FakeLink> if you need any
          more help.
        </FormMessage>
      );

    case 'access_denied':
      if (exceptionDetails === 'credentials_login_disabled') {
        return (
          <FormMessage type={FormMessageType.Error}>
            You can’t log in with {signinMethod || ''} because your organization requires a
            different login method.
          </FormMessage>
        );
      }

      if (exceptionDetails === 'mfa_protected') {
        return <FormMessage type={FormMessageType.Error}>Incorrect verification code.</FormMessage>;
      }

      return (
        <FormMessage type={FormMessageType.Error}>
          You are not allowed to log in to your account.{' '}
          <FakeLink onClick={ChatWidget.handleContactSupport}>Contact us</FakeLink> to get help.
        </FormMessage>
      );

    case 'join_link_expired':
      return (
        <FormMessage type={FormMessageType.Error}>
          Provided join link has expired. <br />
          Please provide your credentials or log in with another service to use your account.
        </FormMessage>
      );

    default:
      return null;
  }
};
